/**
 * Venue Time Formatters
 * @description All venue timezone formatting utilities in one place
 */

// Default venue timezone
export const DEFAULT_VENUE_TIMEZONE = 'America/Los_Angeles';

/**
 * Gets the venue's timezone identifier
 * @param {Object} [venue] - Venue object containing timezone property
 * @returns {string} - The timezone identifier (e.g., 'America/Los_Angeles')
 */
export const getVenueTimezone = (venue = null) => {
  // Match backend logic exactly: venueDoc.timezone || "America/Los_Angeles"
  if (!venue || !venue.timezone) {
    console.warn(`No timezone set for venue${venue && venue.id ? ` (id: ${venue.id})` : ''}. Using default timezone.`);
    return DEFAULT_VENUE_TIMEZONE;
  }
  return venue.timezone;
};

/**
 * Creates a date formatter configured for venue timezone
 * @param {Object} [venue] - Venue object
 * @param {Object} [options] - Additional DateTimeFormat options
 * @returns {Intl.DateTimeFormat} - Configured date formatter
 */
export const createVenueTimeFormatter = (venue = null, options = {}) => {
  const timezone = getVenueTimezone(venue);
  const defaultOptions = {
    timeZone: timezone,
    hour12: true,
    ...options,
  };

  return new Intl.DateTimeFormat('en-US', defaultOptions);
};

/**
 * Format time in venue timezone (e.g., "3:30 PM")
 * @param {Date|string|number} time - Time to format
 * @param {Object} [venue] - Venue object
 * @returns {string} - Formatted time in venue timezone
 */
export const formatTimeInVenueTimezone = (time, venue = null) => {
  if (!time) return '';

  const formatter = createVenueTimeFormatter(venue, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return formatter.format(new Date(time));
};

/**
 * Format time range in venue timezone (e.g., "3:30 PM - 10:00 PM")
 * @param {Date|string|number} startTime - Start time
 * @param {Date|string|number} endTime - End time
 * @param {Object} [venue] - Venue object
 * @returns {string} - Formatted time range in venue timezone
 */
export const formatTimeRangeInVenueTimezone = (startTime, endTime, venue = null) => {
  if (!startTime || !endTime) return '—';

  const start = formatTimeInVenueTimezone(startTime, venue);
  const end = formatTimeInVenueTimezone(endTime, venue);

  return `${start} - ${end}`;
};

/**
 * Format shift time in venue timezone
 * @param {Object} shift - Shift object with start and end properties
 * @param {Object} [venue] - Venue object
 * @returns {string} - Formatted shift time range
 */
export const formatShiftTimeInVenueTimezone = (shift, venue = null) => {
  if (!shift || !(shift.clockedInAt || shift.start) || !(shift.clockedOutAt || shift.end)) return '—';

  return formatTimeRangeInVenueTimezone(shift.clockedInAt || shift.start, shift.clockedOutAt || shift.end, venue);
};

/**
 * Format date in venue timezone
 * @param {Date|string|number} date - Date to format
 * @param {Object} [venue] - Venue object
 * @param {Object} [options] - Additional formatting options
 * @returns {string} - Formatted date in venue timezone
 */
export const formatDateInVenueTimezone = (date, venue = null, options = {}) => {
  if (!date) return '';

  const formatter = createVenueTimeFormatter(venue, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });

  return formatter.format(new Date(date));
};

/**
 * Check if a date is "today" in venue timezone
 * @param {Date|string|number} date - Date to check
 * @param {Object} [venue] - Venue object
 * @returns {boolean} - Whether the date is today in venue timezone
 */
export const isVenueTodayDate = (date, venue = null) => {
  if (!date) return false;

  const venueTimezone = getVenueTimezone(venue);
  const now = new Date();
  const checkDate = new Date(date);

  // Format both dates in venue timezone and compare
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: venueTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const todayString = formatter.format(now);
  const checkDateString = formatter.format(checkDate);

  return todayString === checkDateString;
};
